/* 低階 IndexedDB 助手：只做 Promise 包裝與 schema 宣告，不含產品邏輯。
   刻意不引入外部套件 —— 依賴越少，日後換成 Capacitor 的 SQLite 外掛時越好搬。 */

export function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

export function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("交易被中止"));
  });
}

export interface IndexSpec { name: string; keyPath: string; unique?: boolean }
export interface StoreSpec { name: string; keyPath: string; indexes?: IndexSpec[] }

export const STORES: StoreSpec[] = [
  { name: "concepts", keyPath: "id", indexes: [
    { name: "byCategory", keyPath: "primaryCategoryId" },
    { name: "byDue", keyPath: "reviewDueAt" },
    { name: "byUpdated", keyPath: "updatedAt" } ] },
  { name: "categories", keyPath: "id", indexes: [{ name: "byParent", keyPath: "parentId" }] },
  { name: "links", keyPath: "id", indexes: [
    { name: "byFrom", keyPath: "fromId" }, { name: "byTo", keyPath: "toId" } ] },
  { name: "ops", keyPath: "opId", indexes: [
    { name: "byEntity", keyPath: "entityId" }, { name: "byAt", keyPath: "at" } ] },
  { name: "fingerprints", keyPath: "sha256" },
  { name: "inbox", keyPath: "id" },
  { name: "reviewLogs", keyPath: "id", indexes: [{ name: "byConcept", keyPath: "conceptId" }] },
  { name: "outbox", keyPath: "opId", indexes: [{ name: "byAt", keyPath: "at" }] },
  { name: "blobs", keyPath: "id" },
  { name: "meta", keyPath: "key" },
];

export const DB_NAME = "lifesage";
/** v2：加入 outbox（待推送的 op）。v3：加入 blobs（托盤的素材本體）。
    openDb 只會建立缺少的 store，所以升級是安全的。 */
export const DB_VERSION = 3;

export function openDb(
  factory: IDBFactory, name = DB_NAME, version = DB_VERSION, stores: StoreSpec[] = STORES,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = factory.open(name, version);
    open.onupgradeneeded = () => {
      const db = open.result;
      const upgradeTx = open.transaction;
      if (!upgradeTx) throw new Error("onupgradeneeded 期間沒有交易可用");
      for (const s of stores) {
        const store = db.objectStoreNames.contains(s.name)
          ? upgradeTx.objectStore(s.name)
          : db.createObjectStore(s.name, { keyPath: s.keyPath });
        for (const ix of s.indexes ?? []) {
          if (!store.indexNames.contains(ix.name)) store.createIndex(ix.name, ix.keyPath, { unique: ix.unique ?? false });
        }
      }
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
}

export function getAll<T>(db: IDBDatabase, store: string): Promise<T[]> {
  const tx = db.transaction(store, "readonly");
  return req(tx.objectStore(store).getAll() as IDBRequest<T[]>);
}

export function putValue<T>(db: IDBDatabase, store: string, value: T): Promise<void> {
  const tx = db.transaction(store, "readwrite");
  tx.objectStore(store).put(value as unknown as Record<string, unknown>);
  return txDone(tx);
}

export function deleteValue(db: IDBDatabase, store: string, key: IDBValidKey): Promise<void> {
  const tx = db.transaction(store, "readwrite");
  tx.objectStore(store).delete(key);
  return txDone(tx);
}
